import { MAPS } from '@/maps'
import { useAppStore } from '@/hud-bridge/store'
import { clearAllRecords, formatTime, getBest } from '@/persistence/records'
import type { GameModeId } from '@/gameplay/types'

export function RecordsScreen() {
  const setScreen = useAppStore((s) => s.setScreen)
  const modes: GameModeId[] = ['exit', 'clear']
  const rows = MAPS.flatMap((m) =>
    modes.map((mode) => ({
      key: `${m.id}-${mode}`,
      map: m.displayName,
      mode,
      best: getBest(m.id, mode),
    })),
  )
  const any = rows.some((r) => r.best)

  return (
    <div className="screen">
      <div className="menu-enter" style={{ marginTop: '8vh', maxWidth: 560, width: '100%' }}>
        <h1 className="h1">Records</h1>
        <p className="muted">Личные рекорды (local). Не мировой рейтинг.</p>

        {!any ? (
          <p style={{ marginTop: 24 }}>Пока нет рекордов — сыграй партию.</p>
        ) : (
          <div className="list">
            {rows
              .filter((r) => r.best)
              .map((r) => (
                <div key={r.key} className="record-row">
                  <span>
                    {r.map} · {r.mode === 'exit' ? 'Exit' : 'Clear'}
                  </span>
                  <span style={{ fontFamily: 'var(--font-mono)' }}>
                    {formatTime(r.best!.bestMs)}
                  </span>
                </div>
              ))}
          </div>
        )}

        <div className="btn-row">
          <button type="button" className="btn btn-ghost" onClick={() => setScreen('menu')}>
            Назад
          </button>
        </div>
      </div>
    </div>
  )
}

interface SettingsScreenProps {
  /** По умолчанию — в меню; с паузы — вернуться к Pause */
  onDone?: () => void
}

export function SettingsScreen({ onDone }: SettingsScreenProps) {
  const settings = useAppStore((s) => s.settings)
  const patchSettings = useAppStore((s) => s.patchSettings)
  const setScreen = useAppStore((s) => s.setScreen)

  const handleDone = () => {
    if (onDone) onDone()
    else setScreen('menu')
  }

  return (
    <div className="screen">
      <div className="menu-enter panel" style={{ marginTop: '8vh' }}>
        <h1 className="h1">Settings</h1>

        <div className="field">
          <label htmlFor="sens">
            Чувствительность обзора: {settings.lookSensitivity.toFixed(1)}
          </label>
          <input
            id="sens"
            type="range"
            min={0.3}
            max={2.5}
            step={0.1}
            value={settings.lookSensitivity}
            onChange={(e) => patchSettings({ lookSensitivity: Number(e.target.value) })}
          />
        </div>

        <label className="check">
          <input
            type="checkbox"
            checked={settings.invertY}
            onChange={(e) => patchSettings({ invertY: e.target.checked })}
          />
          Invert Y
        </label>

        <label className="check">
          <input
            type="checkbox"
            checked={settings.leftHandMobile}
            onChange={(e) => patchSettings({ leftHandMobile: e.target.checked })}
          />
          Left-hand mobile
        </label>

        <label className="check">
          <input
            type="checkbox"
            checked={settings.showControlsHints}
            onChange={(e) => patchSettings({ showControlsHints: e.target.checked })}
          />
          Подсказки управления
        </label>

        <div className="field">
          <label>Камера</label>
          <div className="segmented">
            <button
              type="button"
              className={settings.cameraMode === 'fps' ? 'active' : ''}
              onClick={() => patchSettings({ cameraMode: 'fps' })}
            >
              FPS
            </button>
            <button
              type="button"
              className={settings.cameraMode === 'tpp' ? 'active' : ''}
              onClick={() => patchSettings({ cameraMode: 'tpp' })}
            >
              TPP
            </button>
          </div>
        </div>

        <div className="field">
          <label>Ввод</label>
          <div className="segmented">
            <button
              type="button"
              className={settings.preferTouch === null ? 'active' : ''}
              onClick={() => patchSettings({ preferTouch: null })}
            >
              Auto
            </button>
            <button
              type="button"
              className={settings.preferTouch === false ? 'active' : ''}
              onClick={() => patchSettings({ preferTouch: false })}
            >
              Desktop
            </button>
            <button
              type="button"
              className={settings.preferTouch === true ? 'active' : ''}
              onClick={() => patchSettings({ preferTouch: true })}
            >
              Touch
            </button>
          </div>
        </div>

        <div className="btn-row">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              if (confirm('Очистить все локальные рекорды?')) clearAllRecords()
            }}
          >
            Clear records
          </button>
          <button type="button" className="btn btn-primary" onClick={handleDone}>
            Готово
          </button>
        </div>
      </div>
    </div>
  )
}
